/**
 * Local persistence for the terminal chat transcripts.
 *
 * The goal is Telegram-style continuity: a refresh repaints the most recent
 * messages immediately so the user has something to scroll while the gateway
 * reconnects, instead of staring at a blank chat.
 *
 * OpenClaw's `chat.history` RPC is a tail-reader with no cursor pagination
 * (hard max 1000 messages), so the local cache is the primary source of
 * deeper history — caps are intentionally generous to retain weeks of
 * conversation across reloads.
 *
 * Sessions: each `ChatSession` has its own per-session message blob keyed
 * via `config.keyFor('messages', accountId, sessionId)`. The sessions
 * index lives under `config.keyFor('sessions', accountId)`.
 *
 * Hosts call `createChatHistoryStore(config)` with their own key-shape
 * function and feature toggles so existing user data continues to load:
 *
 *   Paradex (prefix-style):  `paradex_terminal_<scope>:<addr>[:<extra>]`
 *   Paradigm (suffix-style): `<userId>:paradigm_terminal_<scope>[:<extra>]`
 *
 * Per-host divergences captured via config:
 *   - `legacyMigration`: 'one-level' (Paradex) | 'two-level' (Desktop)
 *   - `pendingCleanupTracking`: true only for Desktop
 *   - `orphanScanOnClear`: true only for Desktop
 *   - `clearCustomFoldersOnSignOut`: true only for Desktop
 */

import type {
  ChatMessage,
  ChatSession,
  CustomFolder,
  SessionFolder,
} from './types.js';

// --- Configuration ---

export type ChatHistoryScope =
  | 'sessions'
  | 'customFolders'
  | 'messages'
  | 'historyUi'
  | 'tombstones'
  | 'legacyHistory'
  | 'veryLegacyHistory'
  | 'pendingCleanup';

export interface ChatHistoryStoreConfig {
  /**
   * Compute the localStorage key for a given (scope, accountId, ...extra)
   * tuple. Extra parts are typically `sessionId` for per-session message
   * blobs.
   *
   * Paradex example:
   *   keyFor('messages', '0xabc', 'session-1')
   *   → 'paradex_terminal_chat_history:0xabc:session-1'
   *
   * Desktop example:
   *   keyFor('messages', 'user-42', 'session-1')
   *   → 'user-42:paradigm_terminal_chat_history:session-1'
   */
  keyFor(
    scope: ChatHistoryScope,
    accountId: string,
    ...extra: string[]
  ): string;

  /** Default folder for sessions created/migrated by this host. */
  defaultFolder: SessionFolder;

  /**
   * Legacy migration depth.
   * - `'one-level'` — try only the current key, ignore historical shapes.
   * - `'two-level'` — try current, then `veryLegacyHistory` and migrate.
   */
  legacyMigration?: 'one-level' | 'two-level';

  /** Off in Paradex; Desktop sets true and uses the pending-cleanup APIs. */
  pendingCleanupTracking?: boolean;

  /** Off in Paradex; Desktop sets true for defensive prefix scans. */
  orphanScanOnClear?: boolean;

  /** Off in Paradex; Desktop sets true so sign-out also wipes folders. */
  clearCustomFoldersOnSignOut?: boolean;
}

// --- Public constants (re-exported by hosts) ---

/**
 * How long persisted messages stay in localStorage. 30 days keeps roughly
 * a month of conversation visible across reloads — important because
 * OpenClaw's `chat.history` RPC has no cursor pagination.
 */
export const PERSISTED_HISTORY_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
/**
 * Lower bound. If a user has been idle for longer than the retention
 * window, always keep at least this many most-recent messages.
 */
export const PERSISTED_HISTORY_MIN_MESSAGES = 50;
/**
 * Absolute safety cap. Per-origin localStorage caps at ~5MB and a single
 * chat message can easily exceed 1KB.
 */
export const PERSISTED_HISTORY_HARD_CAP = 1500;
/** Maximum stored length for an auto-derived session title. */
export const SESSION_TITLE_MAX_LEN = 200;
/** Hard cap on pending-cleanup retention (Desktop-only). */
export const PENDING_CLEANUP_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

// --- Public types ---

export interface HistoryUiState {
  readonly collapsed: {
    readonly paradex: boolean;
    readonly paradigm: boolean;
  };
  readonly activeSessionId: string | null;
}

const DEFAULT_UI_STATE: HistoryUiState = {
  collapsed: { paradex: false, paradigm: false },
  activeSessionId: null,
};

/**
 * Deletion marker for session-sync. Tombstones ride inside the synced
 * index document so a deletion on this device beats a stale index from
 * another one; locally they survive reloads so offline deletions aren't
 * lost.
 */
export interface DeletionTombstone {
  readonly kind: 'session' | 'folder';
  readonly id: string;
  readonly deletedAt: number;
}

/** Tracked failed sign-out — Desktop-only (no-op in Paradex). */
export interface PendingCleanup {
  readonly userId: string;
  readonly signedOutAt: number;
  readonly lastAttemptAt?: number;
}

// --- Helpers (re-exported as pure functions) ---

export function generateSessionId(): string {
  return generateRandomId('s');
}

export function generateMessageId(prefix: string): string {
  return `${prefix}-${generateRandomId('m')}`;
}

function generateRandomId(fallbackPrefix: string): string {
  const c = typeof crypto !== 'undefined' ? crypto : undefined;
  if (c != null && typeof c.randomUUID === 'function') {
    return c.randomUUID();
  }
  // Fallback for environments without `crypto.randomUUID`. These are local
  // session/message ids — collision-only risk, NOT security tokens — so a
  // `Math.random()` suffix is acceptable here (the device-identity nonce, by
  // contrast, is server-provided, not derived from this).
  return `${fallbackPrefix}-${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2, 10)}`;
}

/**
 * Pull a recognizable title out of a transcript: the first user message,
 * collapsed to one line and clamped to `SESSION_TITLE_MAX_LEN`. Falls back
 * to the first assistant message when there's no user message yet, and to
 * `fallback` when there's no content at all.
 */
export function deriveSessionTitleFromMessages(
  messages: readonly ChatMessage[],
  fallback: string,
): string {
  const candidate =
    messages.find((m) => m.role === 'user' && m.content.trim().length > 0) ??
    messages.find((m) => m.content.trim().length > 0);
  if (candidate == null) return fallback;
  const oneLine = candidate.content.replace(/\s+/gu, ' ').trim();
  if (oneLine.length === 0) return fallback;
  if (oneLine.length <= SESSION_TITLE_MAX_LEN) return oneLine;
  return `${oneLine.slice(0, SESSION_TITLE_MAX_LEN - 1)}…`;
}

// --- Store shape ---

export interface ChatHistoryStore {
  loadSessions(accountId: string): readonly ChatSession[];
  saveSessions(accountId: string, sessions: readonly ChatSession[]): void;

  loadCustomFolders(accountId: string): readonly CustomFolder[];
  saveCustomFolders(accountId: string, folders: readonly CustomFolder[]): void;

  loadSessionMessages(
    accountId: string,
    sessionId: string,
  ): readonly ChatMessage[];
  saveSessionMessages(
    accountId: string,
    sessionId: string,
    messages: readonly ChatMessage[],
  ): void;
  deleteSessionMessages(accountId: string, sessionId: string): void;

  loadHistoryUiState(accountId: string): HistoryUiState;
  saveHistoryUiState(accountId: string, state: HistoryUiState): void;

  loadTombstones(accountId: string): readonly DeletionTombstone[];
  saveTombstones(
    accountId: string,
    tombstones: readonly DeletionTombstone[],
  ): void;
  addTombstone(
    accountId: string,
    kind: DeletionTombstone['kind'],
    id: string,
  ): void;

  migrateLegacyHistoryIfNeeded(accountId: string): readonly ChatSession[];
  recoverOrphanedFolders(
    sessions: readonly ChatSession[],
    existingFolders: readonly CustomFolder[],
  ): { readonly folders: readonly CustomFolder[]; readonly recovered: boolean };
  recoverOrphanedSessions(
    accountId: string,
    existing: readonly ChatSession[],
  ): { readonly sessions: readonly ChatSession[]; readonly recovered: boolean };
  clearChatHistory(accountId: string): void;

  // Desktop-only — no-ops when `pendingCleanupTracking` is false.
  loadPendingCleanups(): readonly PendingCleanup[];
  /** Record (or refresh) a pending-cleanup entry for `userId`. Idempotent. */
  addPendingCleanup(userId: string): void;
  removePendingCleanup(userId: string): void;
  updatePendingCleanup(userId: string, patch: Partial<PendingCleanup>): void;
}

// --- Type guards ---

function isPersistedTombstone(value: unknown): value is DeletionTombstone {
  if (value == null || typeof value !== 'object') return false;
  const t = value as Record<string, unknown>;
  return (
    (t.kind === 'session' || t.kind === 'folder') &&
    typeof t.id === 'string' &&
    typeof t.deletedAt === 'number'
  );
}

function isPersistedPendingCleanup(value: unknown): value is PendingCleanup {
  if (value == null || typeof value !== 'object') return false;
  const c = value as Record<string, unknown>;
  return (
    typeof c.userId === 'string' &&
    c.userId.length > 0 &&
    typeof c.signedOutAt === 'number' &&
    Number.isFinite(c.signedOutAt) &&
    (c.lastAttemptAt === undefined || typeof c.lastAttemptAt === 'number')
  );
}

function isPersistedChatMessage(value: unknown): value is ChatMessage {
  if (value == null || typeof value !== 'object') return false;
  const m = value as Record<string, unknown>;
  return (
    typeof m.id === 'string' &&
    (m.role === 'user' || m.role === 'assistant') &&
    typeof m.content === 'string' &&
    typeof m.timestamp === 'number'
  );
}

function isPersistedCustomFolder(value: unknown): value is CustomFolder {
  if (value == null || typeof value !== 'object') return false;
  const f = value as Record<string, unknown>;
  return (
    typeof f.id === 'string' &&
    typeof f.name === 'string' &&
    typeof f.createdAt === 'number' &&
    (f.pinned === undefined || typeof f.pinned === 'boolean')
  );
}

function isPersistedSession(value: unknown): value is ChatSession {
  if (value == null || typeof value !== 'object') return false;
  const s = value as Record<string, unknown>;
  const folder: SessionFolder | null =
    s.folder === 'paradex' || s.folder === 'paradigm' ? s.folder : null;
  return (
    typeof s.sessionId === 'string' &&
    folder != null &&
    typeof s.title === 'string' &&
    typeof s.pinned === 'boolean' &&
    typeof s.createdAt === 'number' &&
    typeof s.updatedAt === 'number' &&
    (s.customFolderId === undefined || typeof s.customFolderId === 'string') &&
    (s.folderUpdatedAt === undefined ||
      typeof s.folderUpdatedAt === 'number') &&
    (s.clearedAt === undefined || typeof s.clearedAt === 'number')
  );
}

function parseMessages(raw: string | null): readonly ChatMessage[] {
  if (raw == null || raw.length === 0) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isPersistedChatMessage);
  } catch {
    return [];
  }
}

/**
 * Pick the slice of `messages` we want to persist:
 *   1. Drop any message older than `PERSISTED_HISTORY_MAX_AGE_MS`.
 *   2. Always keep at least `PERSISTED_HISTORY_MIN_MESSAGES` of the most
 *      recent so a long-idle user still sees their last conversation.
 *   3. Hard-cap the total at `PERSISTED_HISTORY_HARD_CAP`.
 *   4. Strip the streaming flag — a partially-streamed bubble persisted
 *      with `isStreaming: true` would reload as stuck dots.
 */
function trimForPersistence(
  messages: readonly ChatMessage[],
): readonly ChatMessage[] {
  if (messages.length === 0) return messages;
  const cutoff = Date.now() - PERSISTED_HISTORY_MAX_AGE_MS;
  const withinWindow = messages.filter((m) => m.timestamp > cutoff);
  const kept =
    withinWindow.length >= PERSISTED_HISTORY_MIN_MESSAGES
      ? withinWindow
      : messages.slice(-PERSISTED_HISTORY_MIN_MESSAGES);
  return kept
    .slice(-PERSISTED_HISTORY_HARD_CAP)
    .map((m) => (m.isStreaming === true ? { ...m, isStreaming: false } : m));
}

// --- Factory ---

export function createChatHistoryStore(
  config: ChatHistoryStoreConfig,
): ChatHistoryStore {
  const { keyFor, defaultFolder } = config;
  const twoLevelLegacy = config.legacyMigration === 'two-level';
  const pendingCleanupOn = config.pendingCleanupTracking === true;
  const orphanScanOnClear = config.orphanScanOnClear === true;
  const clearCustomFoldersOnSignOut =
    config.clearCustomFoldersOnSignOut === true;

  // ---- Sessions ----

  function loadSessions(accountId: string): readonly ChatSession[] {
    try {
      const raw = localStorage.getItem(keyFor('sessions', accountId));
      if (raw == null || raw.length === 0) return [];
      const parsed: unknown = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed.filter(isPersistedSession);
    } catch {
      return [];
    }
  }

  function saveSessions(
    accountId: string,
    sessions: readonly ChatSession[],
  ): void {
    try {
      localStorage.setItem(
        keyFor('sessions', accountId),
        JSON.stringify(sessions),
      );
    } catch {
      // best-effort
    }
  }

  // ---- Custom folders ----

  function loadCustomFolders(accountId: string): readonly CustomFolder[] {
    try {
      const raw = localStorage.getItem(keyFor('customFolders', accountId));
      if (raw == null || raw.length === 0) return [];
      const parsed: unknown = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed.filter(isPersistedCustomFolder);
    } catch {
      return [];
    }
  }

  function saveCustomFolders(
    accountId: string,
    folders: readonly CustomFolder[],
  ): void {
    try {
      localStorage.setItem(
        keyFor('customFolders', accountId),
        JSON.stringify(folders),
      );
    } catch {
      // best-effort
    }
  }

  // ---- Session messages ----

  function loadSessionMessages(
    accountId: string,
    sessionId: string,
  ): readonly ChatMessage[] {
    try {
      return parseMessages(
        localStorage.getItem(keyFor('messages', accountId, sessionId)),
      );
    } catch {
      return [];
    }
  }

  function saveSessionMessages(
    accountId: string,
    sessionId: string,
    messages: readonly ChatMessage[],
  ): void {
    try {
      const trimmed = trimForPersistence(messages);
      localStorage.setItem(
        keyFor('messages', accountId, sessionId),
        JSON.stringify(trimmed),
      );
    } catch {
      // Best-effort: intentionally silent. On a write/quota failure the
      // transcript simply stays in memory and is re-fetched/re-merged from the
      // gateway on the next hydration, so there's no data loss — only a missed
      // local cache write. (Core has no logging seam; a host-facing
      // "history not saved locally" signal would belong upstream.)
    }
  }

  function deleteSessionMessages(accountId: string, sessionId: string): void {
    try {
      localStorage.removeItem(keyFor('messages', accountId, sessionId));
    } catch {
      // ignore
    }
  }

  // ---- UI state ----

  function loadHistoryUiState(accountId: string): HistoryUiState {
    try {
      const raw = localStorage.getItem(keyFor('historyUi', accountId));
      if (raw == null || raw.length === 0) return DEFAULT_UI_STATE;
      const parsed: unknown = JSON.parse(raw);
      if (parsed == null || typeof parsed !== 'object') return DEFAULT_UI_STATE;
      const obj = parsed as Record<string, unknown>;
      const collapsed = obj.collapsed as Record<string, unknown> | undefined;
      const activeSessionId =
        typeof obj.activeSessionId === 'string' ? obj.activeSessionId : null;
      return {
        collapsed: {
          paradex: collapsed?.paradex === true,
          paradigm: collapsed?.paradigm === true,
        },
        activeSessionId,
      };
    } catch {
      return DEFAULT_UI_STATE;
    }
  }

  function saveHistoryUiState(accountId: string, state: HistoryUiState): void {
    try {
      localStorage.setItem(
        keyFor('historyUi', accountId),
        JSON.stringify(state),
      );
    } catch {
      // ignore
    }
  }

  // ---- Tombstones ----

  function loadTombstones(accountId: string): readonly DeletionTombstone[] {
    try {
      const raw = localStorage.getItem(keyFor('tombstones', accountId));
      if (raw == null || raw.length === 0) return [];
      const parsed: unknown = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed.filter(isPersistedTombstone);
    } catch {
      return [];
    }
  }

  function saveTombstones(
    accountId: string,
    tombstones: readonly DeletionTombstone[],
  ): void {
    try {
      localStorage.setItem(
        keyFor('tombstones', accountId),
        JSON.stringify(tombstones),
      );
    } catch {
      // best-effort
    }
  }

  function addTombstone(
    accountId: string,
    kind: DeletionTombstone['kind'],
    id: string,
  ): void {
    const existing = loadTombstones(accountId).filter(
      (t) => !(t.kind === kind && t.id === id),
    );
    saveTombstones(accountId, [
      ...existing,
      { kind, id, deletedAt: Date.now() },
    ]);
  }

  // ---- Legacy migration ----

  /**
   * Read the legacy flat-history blob, optionally with two-level fallback
   * (Desktop's pre-suffix shape).
   */
  function readLegacyHistoryRaw(accountId: string): string | null {
    try {
      const current = localStorage.getItem(keyFor('legacyHistory', accountId));
      if (current != null) return current;
    } catch {
      // continue to very-legacy if enabled
    }
    if (!twoLevelLegacy) return null;
    try {
      const veryLegacyKey = keyFor('veryLegacyHistory', accountId);
      const veryLegacy = localStorage.getItem(veryLegacyKey);
      if (veryLegacy != null) {
        try {
          localStorage.setItem(keyFor('legacyHistory', accountId), veryLegacy);
          localStorage.removeItem(veryLegacyKey);
        } catch {
          // best-effort migration
        }
      }
      return veryLegacy;
    } catch {
      return null;
    }
  }

  /**
   * If a legacy flat-history blob exists for this account but the new
   * sessions index doesn't, migrate it into a single session under the
   * default folder so existing users don't lose their transcript.
   */
  function migrateLegacyHistoryIfNeeded(
    accountId: string,
  ): readonly ChatSession[] {
    const existing = loadSessions(accountId);
    if (existing.length > 0) return existing;
    const legacy = readLegacyHistoryRaw(accountId);
    if (legacy == null || legacy.length === 0) return existing;
    const messages = parseMessages(legacy);
    if (messages.length === 0) {
      try {
        localStorage.removeItem(keyFor('legacyHistory', accountId));
      } catch {
        // ignore
      }
      return existing;
    }
    const now = Date.now();
    const session: ChatSession = {
      sessionId: generateSessionId(),
      folder: defaultFolder,
      title: deriveSessionTitleFromMessages(messages, 'Restored chat'),
      pinned: false,
      createdAt: now,
      updatedAt: messages[messages.length - 1]?.timestamp ?? now,
    };
    const migrated = [session];
    saveSessions(accountId, migrated);
    saveSessionMessages(accountId, session.sessionId, messages);
    try {
      localStorage.removeItem(keyFor('legacyHistory', accountId));
    } catch {
      // ignore
    }
    return migrated;
  }

  // ---- Orphan recovery ----

  function recoverOrphanedFolders(
    sessions: readonly ChatSession[],
    existingFolders: readonly CustomFolder[],
  ): {
    readonly folders: readonly CustomFolder[];
    readonly recovered: boolean;
  } {
    const existingIds = new Set(existingFolders.map((f) => f.id));
    const referencedIds = new Set<string>();
    for (const s of sessions) {
      if (s.customFolderId != null && !existingIds.has(s.customFolderId)) {
        referencedIds.add(s.customFolderId);
      }
    }
    if (referencedIds.size === 0) {
      return { folders: existingFolders, recovered: false };
    }
    const now = Date.now();
    let n = 1;
    const orphanFolders: CustomFolder[] = [];
    const sortedIds = [...referencedIds].sort();
    for (const id of sortedIds) {
      orphanFolders.push({
        id,
        name: `Recovered folder ${n}`,
        createdAt: now,
      });
      n += 1;
    }
    return {
      folders: [...existingFolders, ...orphanFolders],
      recovered: true,
    };
  }

  function recoverOrphanedSessions(
    accountId: string,
    existing: readonly ChatSession[],
  ): {
    readonly sessions: readonly ChatSession[];
    readonly recovered: boolean;
  } {
    // The messages-key with empty sessionId is the prefix we scan against.
    const prefix = keyFor('messages', accountId, '');
    const existingIds = new Set(existing.map((s) => s.sessionId));
    const orphans: ChatSession[] = [];
    try {
      for (let i = 0; i < localStorage.length; i += 1) {
        const key = localStorage.key(i);
        if (key == null || !key.startsWith(prefix)) continue;
        const sessionId = key.slice(prefix.length);
        if (sessionId.length === 0) continue;
        if (existingIds.has(sessionId)) continue;
        const messages = loadSessionMessages(accountId, sessionId);
        if (messages.length === 0) continue;
        const lastTs = messages[messages.length - 1]?.timestamp ?? Date.now();
        const firstTs = messages[0]?.timestamp ?? lastTs;
        orphans.push({
          sessionId,
          folder: defaultFolder,
          title: deriveSessionTitleFromMessages(messages, 'Recovered chat'),
          pinned: false,
          createdAt: firstTs,
          updatedAt: lastTs,
        });
      }
    } catch {
      return { sessions: existing, recovered: false };
    }
    if (orphans.length === 0) {
      return { sessions: existing, recovered: false };
    }
    orphans.sort((a, b) => b.updatedAt - a.updatedAt);
    return { sessions: [...existing, ...orphans], recovered: true };
  }

  // ---- Clear ----

  /**
   * Wipe everything associated with this account: sessions index, every
   * per-session message blob, UI state, legacy keys, tombstones. Desktop
   * additionally cleans custom folders and runs a defensive prefix scan
   * to catch stray blobs if the index is corrupt.
   */
  function clearChatHistory(accountId: string): void {
    try {
      const sessions = loadSessions(accountId);
      for (const s of sessions) {
        try {
          localStorage.removeItem(keyFor('messages', accountId, s.sessionId));
        } catch {
          // ignore
        }
      }

      if (orphanScanOnClear) {
        // Collect orphan keys first, then delete — avoids index shift while
        // iterating localStorage.
        const prefix = keyFor('messages', accountId, '');
        const orphanKeys: string[] = [];
        try {
          for (let i = 0; i < localStorage.length; i += 1) {
            const key = localStorage.key(i);
            if (key != null && key.startsWith(prefix)) {
              orphanKeys.push(key);
            }
          }
        } catch {
          // ignore
        }
        for (const key of orphanKeys) {
          try {
            localStorage.removeItem(key);
          } catch {
            // ignore
          }
        }
      }

      localStorage.removeItem(keyFor('sessions', accountId));
      localStorage.removeItem(keyFor('historyUi', accountId));
      localStorage.removeItem(keyFor('legacyHistory', accountId));
      if (twoLevelLegacy) {
        try {
          localStorage.removeItem(keyFor('veryLegacyHistory', accountId));
        } catch {
          // ignore
        }
      }
      localStorage.removeItem(keyFor('tombstones', accountId));
      if (clearCustomFoldersOnSignOut) {
        try {
          localStorage.removeItem(keyFor('customFolders', accountId));
        } catch {
          // ignore
        }
      }
    } catch {
      // ignore
    }
  }

  // ---- Pending cleanup (Desktop-only) ----
  //
  // When `pendingCleanupTracking` is off these are no-ops returning empty
  // results so callers don't need to feature-detect.

  // NOTE on age expiry: the Desktop original does NOT filter by age on
  // read. Bounded retention (PENDING_CLEANUP_MAX_AGE_MS) is enforced by
  // the session-sync layer (`processCleanupEntry`), which detects an
  // expired entry and performs the wipe + remove. `loadPendingCleanups`
  // must therefore return ALL valid entries so that logic can see them;
  // filtering here would silently disable the bounded-retention wipe.
  function loadPendingCleanups(): readonly PendingCleanup[] {
    if (!pendingCleanupOn) return [];
    try {
      const raw = localStorage.getItem(keyFor('pendingCleanup', ''));
      if (raw == null || raw.length === 0) return [];
      const parsed: unknown = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed.filter(isPersistedPendingCleanup);
    } catch {
      return [];
    }
  }

  function savePendingCleanups(entries: readonly PendingCleanup[]): void {
    if (!pendingCleanupOn) return;
    try {
      if (entries.length === 0) {
        localStorage.removeItem(keyFor('pendingCleanup', ''));
      } else {
        localStorage.setItem(
          keyFor('pendingCleanup', ''),
          JSON.stringify(entries),
        );
      }
    } catch {
      // best-effort
    }
  }

  /**
   * Record (or refresh) a pending-cleanup entry for `userId`. Idempotent:
   * a repeat call for the same user updates `signedOutAt` to now and
   * clears the throttle. userId is lowercased so matching is
   * case-insensitive (callers pass mixed-case addresses).
   */
  function addPendingCleanup(userId: string): void {
    if (!pendingCleanupOn) return;
    const lower = userId.toLowerCase();
    const others = loadPendingCleanups().filter((c) => c.userId !== lower);
    savePendingCleanups([
      ...others,
      { userId: lower, signedOutAt: Date.now() },
    ]);
  }

  function removePendingCleanup(userId: string): void {
    if (!pendingCleanupOn) return;
    const lower = userId.toLowerCase();
    savePendingCleanups(
      loadPendingCleanups().filter((c) => c.userId !== lower),
    );
  }

  function updatePendingCleanup(
    userId: string,
    patch: Partial<PendingCleanup>,
  ): void {
    if (!pendingCleanupOn) return;
    const lower = userId.toLowerCase();
    const all = loadPendingCleanups();
    const next = all.map((c) =>
      c.userId === lower ? { ...c, ...patch, userId: c.userId } : c,
    );
    savePendingCleanups(next);
  }

  return {
    loadSessions,
    saveSessions,
    loadCustomFolders,
    saveCustomFolders,
    loadSessionMessages,
    saveSessionMessages,
    deleteSessionMessages,
    loadHistoryUiState,
    saveHistoryUiState,
    loadTombstones,
    saveTombstones,
    addTombstone,
    migrateLegacyHistoryIfNeeded,
    recoverOrphanedFolders,
    recoverOrphanedSessions,
    clearChatHistory,
    loadPendingCleanups,
    addPendingCleanup,
    removePendingCleanup,
    updatePendingCleanup,
  };
}
